import { PayloadAction } from '@reduxjs/toolkit';
import { ApiErrorResponse, ApiResponse } from 'apisauce';
import { isArrayLike } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { forkJoin, from, Observable, Observer, Subscription, timer } from 'rxjs';
import { debounceTime, delay, filter, mergeMap } from 'rxjs/operators';

import config from '../../../config';
import { MOCK } from '../../../configDebug';
import LibraryConfig from '../../../configLib';
import { CurrencyEnum, EnrollChanelEnum, HttpStatusCodeEnum, PredefinedMilisecondsTypeEnum } from '../../enums';
import { CALLS_TO_MEMOIZE, UPDATE_BALANCES_ORDER_DELAY } from '../../libSettings';
import {
  ChangeIndividualUserNameRequest,
  LegalDeclarationsDynamicFilePayload,
  LookupInterface,
  OwnerTradingAccount,
  SearchTradingPlansRequest,
  UserTypeRelation,
} from '../../models/crm/types';
import {
  CountryData,
  EmploymentStatus,
  ENROLL_METHOD,
  FinancialQuestionnaireTypeData,
  IndividualExtendedInfoData,
  PurposeOfTradingData,
  SourceOfFundsOwnData,
  TokenDecodedData,
} from '../../models/enroll';
import {
  AppDocumentUploadCategories,
  ENROLL_AUTH_REQUEST_TYPE,
  EnrollDeleteRequestBody,
  EnrollGetRequestBody,
  EnrollIndividualAddressRequestData,
  EnrollIndividualRequestData,
  EnrollIndividualTaxDetailData,
  EnrollIndividualTaxDetailRequestData,
  EnrollPostRequestBody,
  EnrollPutRequestBody,
  FileUploadRegisterData,
  GenderTypeEnum,
} from '../../models/enroll-requests';
import { CRMService } from '../../services/CRM.service';
import GlobalService from '../../services/Global.service';
import { LoggerService } from '../../services/logger/Logger.service';
import CountriesCache, { updateCountriesCache } from '../../store-util/CountriesCache';
import { isMyPortalTrade } from '../../util/auth/helpers';
import { decodeJwt, isOneOf } from '../../util/DataHelpers';
import { NullableString } from '../../util/types';
import { getManualTokenFailed } from '../auth';
import {
  accountsFailed,
  applicationStartup,
  enrollAuthCompleted,
  enrollSuccess,
  EnrollSuccessPayload,
  getAuthUserTradingAcount,
  getBalances,
  getCountryListSuccess,
  getIndividualExtendedInfo,
  getManualTokenSuccess,
  individualExtendedInfoFailed,
  individualExtendedInfoSuccess,
  logout,
  servicesReady,
  sorOrderEvent,
} from '../common-actions';
import { processErrorInResponse, processResponse } from '../helpers';
import { ALL_TRANSACTIONS_PAGE_SIZE, FIRST_PAGE_NUMBER } from '../payment/constants';
import {
  getToken,
  hasAllDocumentsUploaded,
  hasLegalDeclarations,
  hasReachedUserEnrollStepPendingAcceptance,
  hasValidToken,
  isNotMyPortalTradeOrMyPortalTradeEnrollmentStepsCompleted,
  isUserEnrollStepForVerification,
  isUserEnrollStepPendingAcceptance,
  isUserEnrollStepPendingDocuments,
  isUserVerifiedStatus,
} from '../selectors';
import { getBuyingPower } from '../trading';
import { hasBPUpdated } from '../trading/helpers';
import { RootState } from '..';

import {
  ENROLL_EXTENDED_INFO_TRIGGER_CALLS,
  GET_TRADING_ACCOUNTS_CALL_LIMIT,
  INDIVIDUAL_EXTENDED_INFO_CALL_NAME,
} from './constants';
import { getBinaryDocumentRequestBody, getBinaryPOIDocumentRequestBody, hasCapitalSubscription } from './helpers';
import {
  accountDetailsCompleted,
  accountDetailsFailed,
  accountInfoCompleted,
  accountInfoFailed,
  accountsCompleted,
  changeIndividualUserName,
  changeIndividualUserNameFail,
  changeIndividualUserNameSuccess,
  enroll,
  enrollAddCallToQueue,
  enrollAddressesBatch,
  enrollAddressesBatchFail,
  enrollAddressesBatchSuccess,
  enrollFail,
  enrollPersonalDetailsFailed,
  enrollPersonalDetailsSuccess,
  enrollPersonalDetailsWithTaxDetails,
  getAccountsSummary,
  getAccountsSummaryFailed,
  getAccountsSummarySuccess,
  getCountryList,
  getCountryListFailed,
  getEnrollFinancialQuestionnaire,
  getEnrollFinancialQuestionnaireFail,
  getEnrollFinancialQuestionnaireSuccess,
  getIndividualUserName,
  getIndividualUserNameFail,
  getIndividualUserNameSuccess,
  getLegalDeclarations,
  getLegalDeclarationsCompleted,
  getLegalDeclarationsDynamicFile,
  getLegalDeclarationsDynamicFileFail,
  getLegalDeclarationsDynamicFileSuccess,
  getLegalDeclarationsFailed,
  getNextAccount,
  getTradingPlatforms,
  getTradingPlatformsFailed,
  getTradingPlatformsSuccess,
  internalTransfer,
  internalTransferFailed,
  internalTransferSuccess,
  refreshSubscription,
  refreshSubscriptionFailed,
  refreshSubscriptionSuccess,
  searchTradingPlans,
  searchTradingPlansFail,
  searchTradingPlansSuccess,
  updateIndividualTransitionToForActive,
  updateIndividualTransitionToForActiveFail,
  updateIndividualTransitionToForActiveSuccess,
  updateIndividualTransitionToForVerification,
  updateIndividualTransitionToForVerificationFail,
  updateIndividualTransitionToForVerificationSuccess,
} from './index';

const logger = LoggerService.getLogger();

const extendedInfoEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(
    getIndividualExtendedInfo.type,
    servicesReady.type,
    enrollSuccess.type,
    enrollAddressesBatchSuccess.type,
    enrollPersonalDetailsSuccess.type,
  ),
  filter(action => {
    const enrollAccessToken = state$.value.auth.enroll.accessToken;
    const authAccessToken = state$.value.auth.accessToken;

    // Return false if both tokens are unavailable
    if (!enrollAccessToken && !authAccessToken) return false;

    // Skip filter if enroll access token is defined and auth access token is undefined (enroll flow)
    if (enrollAccessToken && !authAccessToken) return true;

    // Otherwise, perform the rest checks
    return action.type === enrollSuccess.type
      ? ENROLL_EXTENDED_INFO_TRIGGER_CALLS.includes(ENROLL_METHOD[action.payload?.callName] as any)
        && state$.value.crm.enroll.callsInQueue === 0
      : hasValidToken(state$.value);
  }),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    let callName = INDIVIDUAL_EXTENDED_INFO_CALL_NAME;
    const enrollAccessToken = state$.value.auth.enroll.accessToken;
    const authAccessToken = state$.value.auth.accessToken;

    // Call with enrollToken when accessToken is undefined (enroll flow)
    if (enrollAccessToken && !authAccessToken) {
      GlobalService
        .crmService
        .enrollGet(ENROLL_METHOD[ENROLL_METHOD.getIndividualExtendedInfo], [], enrollAccessToken)
        .then((response: ApiResponse<any>) => {
          processResponse(
            callName,
            {},
            response,
            observer,
            individualExtendedInfoSuccess,
            individualExtendedInfoFailed,
          );
        })
        .catch(error => processErrorInResponse(callName, error, action, observer, individualExtendedInfoFailed));
    } else {
      // Regular call with auth token
      GlobalService
        .crmService
        .enrollGet(ENROLL_METHOD[ENROLL_METHOD.getIndividualExtendedInfo])
        .then((response: ApiResponse<any>) => {
          processResponse(
            callName,
            {},
            response,
            observer,
            individualExtendedInfoSuccess,
            individualExtendedInfoFailed,
          );
        })
        .catch(error => processErrorInResponse(callName, error, action, observer, individualExtendedInfoFailed));
    }
  })),
);

const getAccountReferenceIdListEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => {
  let subscription: Subscription;
  let callCaount = 0;

  return action$.pipe(
    ofType(individualExtendedInfoSuccess.type, getAuthUserTradingAcount.type),
    delay(isMyPortalTrade() ? PredefinedMilisecondsTypeEnum.oneSecond : 0), // To take a breath after get enroll portal status
    filter(action => !state$.value.auth.enrollPending),
    filter(action => isUserVerifiedStatus(action.payload) || MOCK.ENABLED),
    filter(action => hasValidToken(state$.value)),
    filter(action => isNotMyPortalTradeOrMyPortalTradeEnrollmentStepsCompleted(state$.value.auth.enrollPortalStatus)),
    mergeMap(action => new Observable((observer: Observer<any>) => {
      const callName = 'crm/accounts';
      if (callCaount >= GET_TRADING_ACCOUNTS_CALL_LIMIT) {
        logger.info('The call count exceeds the limit! Redirecting user to the login');
        subscription?.unsubscribe();
        observer.next(logout());
        return;
      }

      GlobalService
        .crmService
        .getAccountsSummary({
          customerId: state$.value.crm.individualId as number,
          userTypeRelation: UserTypeRelation.Individual,
        })
        .then((response: ApiResponse<OwnerTradingAccount[]>) => {
          const data = response.data?.filter(item => !item.is_cash_account)// only trading accounts we need
            .map(item => item.reference_id);// Manipulate response as a string array
          return { ...response, data };
        })
        .then((response: ApiResponse<any>) => {
          if (isEmpty(response.data)) {
            logger.info('There is no trading account! The same endpoint is called repeatedly by waiting for 2 seconds');
            subscription = timer(PredefinedMilisecondsTypeEnum.threeSeconds)
              .subscribe(() => {
                callCaount++;
                observer.next(getAuthUserTradingAcount(action.payload)); // Re-dispatch the action
              });
            return;
          }

          subscription?.unsubscribe();
          processResponse(callName, {}, response, observer, accountsCompleted, accountsFailed);
        })
        .catch(error => {
          subscription?.unsubscribe();
          processErrorInResponse(callName, error, action, observer, accountsFailed);
        });
    })),
  );
};

const accountsReadyEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(accountsCompleted.type, getNextAccount.type),
  // TODO: Add unit tests for epic and actions ART-4393
  filter(action => !isEmpty(action.payload)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    let callName = 'crm/accountInfo';
    let accountReferenceId: NullableString;

    if (action.type === getNextAccount.type) accountReferenceId = action.payload.account;
    if (action.type === accountsCompleted.type) accountReferenceId = action.payload[state$.value.crm.selectedAccount];

    GlobalService
      .crmService
      .getAccountInfo(accountReferenceId!)
      .then((response: any) => {
        processResponse(
          callName,
          { accountReferenceId },
          response,
          observer,
          accountInfoCompleted,
          accountInfoFailed,
        );
      })
      .catch((error: any) => processErrorInResponse(callName, error, action, observer, accountInfoFailed));
  })),
);

const accountInfoReadyEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(accountInfoCompleted.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    if (!(action.payload instanceof Array) || action.payload.length === 0) {
      observer.next(accountDetailsFailed({ error: new Error('Invalid account info data in action'), errorData: action }));
      return;
    }

    // TODO: Any better way to get the account_id
    const { account_id } = action.payload[0];
    let callName = 'crm/accountDetails';

    GlobalService
      .crmService
      .getAccountDetails(account_id)
      .then((response: any) => {
        processResponse(callName, { account_id }, response, observer, accountDetailsCompleted, accountDetailsFailed);
      })
      .catch((error: any) => processErrorInResponse(callName, error, action, observer, accountDetailsFailed));
  })),
);

const getBalancesEpic = (action$: Observable<any>) => action$.pipe(
  ofType(sorOrderEvent.type),
  debounceTime(UPDATE_BALANCES_ORDER_DELAY),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { status } = action.payload;

    if (hasBPUpdated(status)) {
      observer.next(getBuyingPower());
    } else {
      observer.next(getBalances());
    }
  })),
);

const getCountryListEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(applicationStartup.type, getCountryList.type),
  filter(() => !CountriesCache.get()?.length || !CALLS_TO_MEMOIZE.getCountryList),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/getCountryList';

    GlobalService
      .crmService
      .getCountryList()
      .then(response => {
        if (processResponse(callName, null, response, observer, getCountryListSuccess, getCountryListFailed)) {
          updateCountriesCache(response.data);
        } else {
          updateCountriesCache([]);
        }
      })
      .catch(error => {
        updateCountriesCache([]);
        processErrorInResponse(callName, error, action, observer, getCountryListFailed);
      });
  })),
);

const enrollEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(enroll.type),
  filter(() => hasValidToken(state$.value)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const {
      callMethod, callMethodName: callName, params, requestBody, isQueued, id, callNo,
    } = action.payload;

    if (state$.value.crm.enroll.callsInQueue > 1 && !isQueued) {
      observer.next(enrollAddCallToQueue(action.payload));
      return;
    }

    // TODO: Fix this case with getIndividualFilesCompleted producing an enroll actions with payload {}
    //       Dispatching `enrollSuccess` is needed for the purpose of isQueued - if any action is queued
    if (!callName) {
      observer.next(enrollSuccess({ callName: 'unknown', originalAction: action } as any));
      logger.warn('[crm/epics] enrollEpic - unknown action', action);
      return;
    }

    const isGet = callName.indexOf('get') === 0;
    const isPost = callName.indexOf('post') === 0;
    const isPut = callName.indexOf('put') === 0;
    const isDelete = callName.indexOf('delete') === 0;

    let promiseCreator: () => Promise<any>;
    let customData: any;

    if (isGet) {
      promiseCreator = () => GlobalService.crmService.enrollGet(callName, params);
    } else if (isPost) {
      const { accessToken } = state$.value.auth;
      const documentOwnBinary = callMethod === ENROLL_METHOD.postDocumentOwnBinary;
      const individualIdentityDetailOwnWithFile = callMethod === ENROLL_METHOD.postIndividualIdentityDetailOwnWithFile;
      const { files, filesInfo: filesInfoRaw } = requestBody || {};
      const filesInfo: FileUploadRegisterData[] = isArrayLike(filesInfoRaw) ? filesInfoRaw : [ filesInfoRaw ];
      const firstFileInfo: FileUploadRegisterData = isArrayLike(filesInfoRaw) ? filesInfoRaw[0] : filesInfoRaw;

      if (documentOwnBinary) {
        const fileNames = filesInfo.map(({ fileName }) => fileName);
        const payload = getBinaryDocumentRequestBody(files, firstFileInfo, state$.value);
        promiseCreator = () => GlobalService.crmService.registerBinaryDocument(payload, accessToken!, fileNames);
      } else if (individualIdentityDetailOwnWithFile) {
        const fileNames = filesInfo.map(({ fileName }) => fileName);
        const payload = getBinaryPOIDocumentRequestBody(files, firstFileInfo, state$.value);
        promiseCreator = () => GlobalService.crmService.registerBinaryDocument(payload, accessToken!, fileNames);
      } else {
        promiseCreator = () => GlobalService.crmService.enrollPost(callName, requestBody);
      }
    } else if (isPut) {
      promiseCreator = () => GlobalService.crmService.enrollPut(callName, requestBody, params);
    } else if (isDelete) {
      promiseCreator = () => GlobalService.crmService.enrollDelete(callName, id);
    } else {
      logger.debug(`[crm/epics] enrollEpic - '${callName}' is not recognised`, action.payload);
      throw new Error(`[crm/epics] enrollEpic - '${callName}' is not recognised`);
    }
    if (callNo) customData = { ...customData, callNo };

    promiseCreator()
      .then((response: any) => {
        processResponse(callName, {}, response, observer, enrollSuccess, enrollFail, false, customData);
      })
      .catch((error: any) => processErrorInResponse(callName, error, action, observer, enrollFail));
  })),
);

const enrollCallQueueEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(
    enrollSuccess.type,
    enrollFail.type,
    enrollPersonalDetailsSuccess.type,
    enrollPersonalDetailsFailed.type,
  ),
  filter(() => state$.value.crm.enrollQueued.length > 0),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    observer.next(enroll({ ...state$.value.crm.enrollQueued[0], isQueued: true }));
  })),
);

// TODO: Clean the usage of external variables when possible (was done for quick impl of adding poi id parameter to id document registration)
const POI_FILENAMES = [
  AppDocumentUploadCategories.ID_BACK.fileName,
  AppDocumentUploadCategories.ID_FRONT.fileName,
];

/**
 * This epic implements switching the user individual status to statuses 101, 102 and 103
 * It is organised in 2 cases:
 *    1. transition state from 100 to 101 - pending documents
 *    2. This one has 3 substeps
 *      2.1 transition state from 101 to 102 - pending acceptance
 *      2.2 transition state from 102 to 103 - for verification
 *      2.3 initiate risk assessment /inherited from old onboarding/
 */
const enrollTransitionsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(
    individualExtendedInfoSuccess.type,
    enrollSuccess.type,
    enrollAuthCompleted.type,
  ),
  filter(action => {
    const {
      isTaxDetailsCall, isAcceptLegalDeclarationsCall, isRegisterDocumentCall, isPhoneVerifiedCall,
    } = checkEnrollActionType(action);
    return isTaxDetailsCall || isAcceptLegalDeclarationsCall || isRegisterDocumentCall || isPhoneVerifiedCall;
  }),
  delay(1000), // this aids in waiting for extended info to be refreshed
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const {
      isTaxDetailsCall,
      isAcceptLegalDeclarationsCall,
      isRegisterDocumentCall,
      isPhoneVerifiedCall,
    } = checkEnrollActionType(action);

    let calls = [] as {
      callMethod: ENROLL_METHOD
      params?: any
    }[];
    if (isTaxDetailsCall && !isUserEnrollStepPendingDocuments(state$.value.crm.individualExtendedInfo || null)
    && state$.value.crm.enroll.callMethod !== ENROLL_METHOD.putIndividualTransitionToPendingDocuments) {
      // 1. one call - transition from 100 to 101 - pending documents
      calls = [ { callMethod: ENROLL_METHOD.putIndividualTransitionToPendingDocuments } ];
    } else {
      if ((isRegisterDocumentCall || isPhoneVerifiedCall)
      && hasAllDocumentsUploaded(state$.value)
      && !isUserEnrollStepForVerification(state$.value.crm.individualExtendedInfo)
      ) {
      /*
        2. 3 calls:
          2.1 transition from 100 to 101 - pending acceptance
          2.2 transition from 101 to 102 - for verification
       */
        if (!hasReachedUserEnrollStepPendingAcceptance(state$.value.crm.individualExtendedInfo)) {
          calls.push({ callMethod: ENROLL_METHOD.putIndividualTransitionToPendingAcceptance });
        }
      }
      if (isAcceptLegalDeclarationsCall && hasLegalDeclarations(state$.value)) {
        calls.push({ callMethod: ENROLL_METHOD.putIndividualTransitionToForVerification });
      }
    }

    if (calls.length > 0) {
      if (state$.value.crm.individualId === null) {
        logger.error('[crm/index] enrollTransitionsEpic - individual id not found');
        return;
      }
      calls
        .forEach(call => {
          const { callMethod, params } = call;
          observer.next(enroll({
            callMethod,
            callMethodName: ENROLL_METHOD[callMethod],
            params: params ?? [ (state$.value.crm.individualId!).toString() ],
          }));
        });
    }
  })),
);

const enrollPersonalDetailsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(enrollPersonalDetailsWithTaxDetails.type),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/enrollmentOwnPersonalPutOrPost';

    const { isPersonalDetailsUpdate, individualData } = action.payload;

    const requestBody = { ...individualData } as EnrollIndividualRequestData;

    // Temporary fix, must be deleted when fully integrated new login

    const individual = state$.value.crm.individualExtendedInfo;

    if (isPersonalDetailsUpdate && individual) {
      requestBody.id = individual.id;
      requestBody.user_correlation_id = individual.user_correlation_id;
    } else {
      const { tradingPlatformId, enroll: enrollState } = state$.value.crm;
      requestBody.trading_platform_id = tradingPlatformId;
      requestBody.initially_selected_plan_id = enrollState.initiallySelectedPlanId;
    }

    const params = (individual && individual.id) ? [ individual.id ] : undefined;
    const crmService = GlobalService.crmService;
    const request = isPersonalDetailsUpdate
      ? crmService.enrollPut(ENROLL_METHOD[ENROLL_METHOD.putEnrollmentOwnPersonal], individualData, params)
      : crmService.enrollPost(ENROLL_METHOD[ENROLL_METHOD.postEnrollmentOwnPersonal], requestBody);

    request
      .then(((response: any) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          enrollPersonalDetailsSuccess,
          enrollPersonalDetailsFailed,
          false,
          { individualData, isPersonalDetailsUpdate },
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        enrollPersonalDetailsFailed,
        { isPersonalDetailsUpdate },
      ));
  })),
);

const enrollTaxDetailsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(enrollPersonalDetailsWithTaxDetails.type),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    const { taxDetails, isAddAnotherTaxCountryClicked } = action.payload;
    const individual = state$.value.crm.individualExtendedInfo;
    const countries = CountriesCache.get();

    if (countries) {
      const countryIds = countries.map((country: CountryData) => country.id);

      const registerAction = createTaxDetailsAction(countryIds, individual, taxDetails);
      const updateAction = createTaxDetailsUpdatAction(countryIds, individual, taxDetails);
      const deleteAction = createTaxDetailsDeleteAction(individual, isAddAnotherTaxCountryClicked);

      if (registerAction && registerAction.length > 0) {
        registerAction.map(registeredValue => observer.next(registeredValue));
      }

      if (updateAction && updateAction.length > 0) {
        updateAction.map(updatedValue => observer.next(updatedValue));
      }

      if (deleteAction) {
        observer.next(deleteAction);
      }
    }
  })),
);

const createTaxDetailsAction = (
  countryIds: string[],
  individual: IndividualExtendedInfoData | null,
  taxDetails: EnrollIndividualTaxDetailRequestData,
) => {
  let models: EnrollIndividualTaxDetailData[];
  let callNo: number[] = [];

  models = taxDetails.filter((detail: EnrollIndividualTaxDetailData, detailIndex: number) => {
    const individualTaxDetail = individual ? individual.tax_details[detailIndex] : null;
    const hasPostRequest = (
      !individualTaxDetail
      && detail.tax_number
      && detail.country_id
      && countryIds.includes(detail.country_id)
    );

    if (hasPostRequest) {
      callNo.push(detailIndex + 1);
    }

    return hasPostRequest;
  });

  const callMethod = ENROLL_METHOD.postIndividualTaxDetailOwnBatch;
  if (models.length > 0) {
    return models.map((el, index) => enroll({
      callMethod,
      callMethodName: ENROLL_METHOD[callMethod],
      requestBody: [ el ],
      callNo: callNo[index],
    } as EnrollPostRequestBody));
  }

  return null;
};

const createTaxDetailsUpdatAction = (
  countryIds: string[],
  individual: IndividualExtendedInfoData | null,
  taxDetails: EnrollIndividualTaxDetailRequestData,
) => {
  let callNo: number[] = [];
  const filteredTaxDetail = taxDetails.filter((detail: EnrollIndividualTaxDetailData, detailIndex: number) => {
    const individualTaxDetail = individual ? individual.tax_details[detailIndex] : null;
    let hasUpdateRequest = (
      countryIds.includes(detail.country_id)
      && individualTaxDetail
      && (detail.id === individualTaxDetail.id)
      && (
        (detail.country_id !== individualTaxDetail.country_id)
        || (detail.tax_number !== individualTaxDetail.tax_number))
    );
    if (hasUpdateRequest) {
      callNo.push(detailIndex + 1);
    }
    return hasUpdateRequest;
  });

  if (filteredTaxDetail.length > 0) {
    const callMethod = ENROLL_METHOD.putIndividualTaxDetailOwn;
    return filteredTaxDetail.map((el, index) => enroll({
      callMethod,
      callMethodName: ENROLL_METHOD[callMethod],
      requestBody: el,
      callNo: callNo[index],
    } as EnrollPutRequestBody));
  }

  return null;
};

const createTaxDetailsDeleteAction = (
  individual: IndividualExtendedInfoData | null,
  isAddAnotherTaxCountryClicked: boolean,
) => (
  individual && individual.tax_details.length > 1 && isAddAnotherTaxCountryClicked === false
    ? enroll({
      callMethod: ENROLL_METHOD.deleteTaxDetail,
      callMethodName: ENROLL_METHOD[ENROLL_METHOD.deleteTaxDetail],
      id: individual.tax_details[1].id,
      callNo: 2,
    } as EnrollDeleteRequestBody)
    : null
);

const enrollAddressesBatchEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(enrollAddressesBatch.type),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    const {
      callMethodName: callName,
      addresses,
    } = action.payload;
    const individual = state$.value.crm.individualExtendedInfo;
    let requestStack = [];

    if (addresses.length > 0) {
      requestStack = addresses.map((address: EnrollIndividualAddressRequestData) => {
        if (address.id) {
          const params = (individual && individual.id) ? [ individual.id ] : undefined;

          return GlobalService.crmService.enrollPut(callName, address, params);
        }

        return GlobalService.crmService.enrollPost(callName, address);
      });
    }

    Promise.all(requestStack)
      .then(((response: any) => {
        processResponse(
          callName,
          {},
          response,
          observer,
          enrollAddressesBatchSuccess,
          enrollAddressesBatchFail,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, enrollAddressesBatchFail));
  })),
);

const getEnrollFinancialQuestionnaireEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getEnrollFinancialQuestionnaire.type),
  filter(() => (
    !state$.value.crm.enroll.financialQuestionnaireSections?.length
    || !CALLS_TO_MEMOIZE.financialQuestionnaire
  )),
  mergeMap(action => {
    const callName = 'crm/getEnrollFinancialQuestionnaire';

    /**
     * NB!
     * The order of the items in this `requestStack` array is important
     * Must be left as is.
     * */
    const requestStack: Observable<any>[] = [
      from(GlobalService.crmService.getFinancialQuestionnaireTypes()),
      from(GlobalService.crmService.getFinancialQuestionnaireList()),
      from(GlobalService.crmService.getPurposeOfTrading()),
      from(GlobalService.crmService.getEmploymentStatusList()),
      from(GlobalService.crmService.getSourceOfFundOwn(true)),
    ];

    return forkJoin(requestStack).pipe(
      mergeMap((responses: ApiResponse<any>[]) => {
        const errors: ApiErrorResponse<any>[] = [];

        responses.forEach((response: ApiResponse<any>) => {
          if (response.status !== HttpStatusCodeEnum.Ok) {
            errors.push(response as ApiErrorResponse<any>);
          }
        });

        let types: LookupInterface = {};
        let answers: FinancialQuestionnaireTypeData[] = [];
        let purposeOfTrading: PurposeOfTradingData[] = [];
        let employmentStatusList: EmploymentStatus[] = [];
        let sourceOfFundList: SourceOfFundsOwnData[] = [];

        if (responses[0].data) {
          types = responses[0].data;
        }

        if (responses[1].data && Array.isArray(responses[1].data)) {
          answers = responses[1].data;
        }

        if (responses[2].data && Array.isArray(responses[2].data)) {
          purposeOfTrading = responses[2].data;
        }

        if (responses[3].data && Array.isArray(responses[3].data)) {
          employmentStatusList = responses[3].data;
        }

        if (responses[4].data && Array.isArray(responses[4].data)) {
          sourceOfFundList = responses[4].data;
        }

        return new Observable((observer: Observer<any>) => {
          if (errors.length > 0) {
            processErrorInResponse(callName, errors, action, observer, getEnrollFinancialQuestionnaireFail);
          }

          processResponse(
            callName,
            {},
            responses,
            observer,
            getEnrollFinancialQuestionnaireSuccess,
            getEnrollFinancialQuestionnaireFail,
            false,
            {
              types,
              answers,
              purposeOfTrading,
              sourceOfFundList,
              employmentStatusList,
            },
          );
        });
      }),
    );
  }),
);

const getTradingPlatformsEpic = (action$: Observable<any>) => action$.pipe(
  ofType(getTradingPlatforms.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'getTradingPlatforms';
    GlobalService
      .crmService
      .getTradingPlatforms()
      .then((response: any) => {
        processResponse(
          callName,
          null,
          response,
          observer,
          getTradingPlatformsSuccess,
          getTradingPlatformsFailed,
          false,
          response,
        );
      })
      .catch((error: any) => processErrorInResponse(callName, error, action, observer, getManualTokenFailed));
  })),
);

const refreshSubscriptionEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(refreshSubscription.type),
  filter(() => !hasCapitalSubscription(state$.value.crm.individualExtendedInfo)),
  filter(() => isUserVerifiedStatus(state$.value.crm.individualExtendedInfo)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'refreshSubscription';
    GlobalService
      .crmService
      .refreshSubscription()
      .then((response: any) => {
        processResponse(
          callName,
          null,
          response,
          observer,
          refreshSubscriptionSuccess,
          refreshSubscriptionFailed,
          false,
          response,
        );
      })
      .catch((error: any) => processErrorInResponse(callName, error, action, observer, refreshSubscriptionFailed));
  })),
);

const createIndividualEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getManualTokenSuccess.type, getTradingPlatformsSuccess.type),
  filter(() => hasValidToken(null, state$.value.auth.enroll.accessToken)),
  filter(() => !state$.value.crm.individualExtendedInfo),
  filter(() => state$.value.crm.tradingPlatformId !== undefined),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    const {
      enroll: {
        email,
        firstName,
        lastName,
        country,
      },
    } = state$.value.auth;
    const { tradingPlatformId } = state$.value.crm;
    const token = getToken(state$.value, false)!;

    const tokenDecoded = decodeJwt(token) as TokenDecodedData;
    const { email: tokenEmail } = tokenDecoded;

    if (tokenEmail !== email && !!email) {
      logger.error(`[crm/epics] createIndividualEpic - emails do not match from-token: ${tokenEmail} cached: ${email}`);
      return;
    }
    const temporaryCrmService = new CRMService(state$.value.auth.enroll.accessToken!);

    // create individual
    const callMethodIndividual = ENROLL_METHOD[ENROLL_METHOD.postEnrollmentOwnPersonal];
    const requestBodyIndividual = {
      first_name: firstName,
      last_name: lastName,
      birth_place_id: country,
      nationalities: [ country ],
      gender: GenderTypeEnum.unspecified,
      enroll_channel: LibraryConfig.isWeb ? EnrollChanelEnum.Web : EnrollChanelEnum.Mobile,
      trading_platform_id: tradingPlatformId,
    } as any;

    requestBodyIndividual.initially_selected_plan_id = state$.value.crm.enroll.initiallySelectedPlanId;

    // create residential address
    const callMethodAddress = ENROLL_METHOD[ENROLL_METHOD.postIndividualAddressOwn];
    const requestBodyAddress = {
      address_type: 'R',
      country_id: country,
    };

    temporaryCrmService
      .enrollPost(callMethodIndividual, requestBodyIndividual)
      .then(response => {
        // TODO: error handling if respone body has error
        temporaryCrmService.enrollPost(callMethodAddress, requestBodyAddress);
      })
      .catch(error => {
        // TODO: error handling
      });
  })),
);

// eslint-disable-next-line max-len
const getOnboardingLegalAndQuestionsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(servicesReady.type, individualExtendedInfoSuccess.type),
  filter(action => (
    action.type === servicesReady.type
    || isUserEnrollStepPendingAcceptance(state$.value.crm.individualExtendedInfo)
    || isUserEnrollStepForVerification(state$.value.crm.individualExtendedInfo)
  )),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callMethod = ENROLL_METHOD.getIndividualOwnLegalDeclarations;

    observer.next(
      enroll({
        callMethod,
        callMethodName: ENROLL_METHOD[callMethod],
      } as EnrollGetRequestBody),
    );

    observer.next(getEnrollFinancialQuestionnaire());
  })),
);

export function checkEnrollActionType(action: PayloadAction<EnrollSuccessPayload>) {
  const { callName } = action.payload;
  const isTaxDetailsCall = (
    callName === ENROLL_METHOD[ENROLL_METHOD.postIndividualTaxDetailOwnBatch]
    || callName === ENROLL_METHOD[ENROLL_METHOD.putIndividualTaxDetailOwn]
    || callName === ENROLL_METHOD[ENROLL_METHOD.deleteTaxDetail]
  );
  // TODO: Edit in ART-3128
  const isRegisterDocumentCall = isOneOf(callName, [
    ENROLL_METHOD[ENROLL_METHOD.postIndividualDocumentOwn],
    ENROLL_METHOD[ENROLL_METHOD.postDocumentOwnBinary],
  ]);
  const isAcceptLegalDeclarationsCall = callName === ENROLL_METHOD[ENROLL_METHOD.putIndividualOwnLegalDeclarations];
  const isPhoneVerifiedCall = ENROLL_AUTH_REQUEST_TYPE[callName.split('/')[1] as any] === ENROLL_AUTH_REQUEST_TYPE.VERIFY_PHONE_CODE as any;

  return {
    isTaxDetailsCall,
    isRegisterDocumentCall,
    isAcceptLegalDeclarationsCall,
    isPhoneVerifiedCall,
  };
}

const getLegalDeclarationsEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getLegalDeclarations.type),
  mergeMap(
    (action: PayloadAction<{ active: boolean, isVerified: boolean }>) => new Observable((observer: Observer<any>) => {
      const callName = 'crm/getLegalDeclarationsList';
      const { active, isVerified } = action.payload;
      GlobalService
        .crmService
        .getLegalDeclarationsList(active, isVerified)
        .then((response => {
          processResponse(
            callName,
            {},
            response,
            observer,
            getLegalDeclarationsCompleted,
            getLegalDeclarationsFailed,
            false,
            { isVerified },
          );
        }))
        .catch(error => processErrorInResponse(callName, error, action, observer, getLegalDeclarationsFailed));
    }),
  ),
);

const getLegalDeclarationsDynamicFileNameEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(getLegalDeclarationsDynamicFile.type),
  mergeMap((action: PayloadAction<LegalDeclarationsDynamicFilePayload>) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/getLegalDeclarationsDynamicFile';
    const { fileName, templateName } = action.payload;

    GlobalService
      .crmService
      .getLegalDeclarationsDynamicFile(templateName)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getLegalDeclarationsDynamicFileSuccess,
          getLegalDeclarationsDynamicFileFail,
          false,
          { fileName },
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, getLegalDeclarationsDynamicFileFail));
  })),
);

const updateIndividualTransitionToForVerificationEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(updateIndividualTransitionToForVerification.type),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/updateIndividualTransitionToForVerification';

    GlobalService
      .crmService
      .updateIndividualTransitionToForVerification(state$.value.crm.individualId as number)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          updateIndividualTransitionToForVerificationSuccess,
          updateIndividualTransitionToForVerificationFail,
          false,
          {},
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        updateIndividualTransitionToForVerificationFail,
      ));
  })),
);

const updateIndividualTransitionToForActiveEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(updateIndividualTransitionToForActive.type),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/updateIndividualTransitionToForActive';

    GlobalService
      .crmService
      .updateIndividualTransitionToForActive({
        id: state$.value.crm.individualId as number,
        trading_plan_id: state$.value.crm.individualExtendedInfo?.initially_selected_plan_id as string,
      })
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          updateIndividualTransitionToForActiveSuccess,
          updateIndividualTransitionToForActiveFail,
          false,
          {},
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        updateIndividualTransitionToForActiveFail,
      ));
  })),
);

const getIndividualUserNameEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getIndividualUserName.type),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/getIndividualUserName';

    GlobalService
      .crmService
      .getIndividualUserName(
        state$.value.crm.individualId as number,
      )
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getIndividualUserNameSuccess,
          getIndividualUserNameFail,
          false,
          {},
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getIndividualUserNameFail,
      ));
  })),
);

const changeIndividualUserNameEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(changeIndividualUserName.type),
  mergeMap((action: PayloadAction<ChangeIndividualUserNameRequest>) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/changeIndividualUserName';
    const { individual_id, profile_name } = action.payload;

    GlobalService
      .crmService
      .changeIndividualUserName({
        individual_id,
        profile_name,
      })
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          changeIndividualUserNameSuccess,
          changeIndividualUserNameFail,
          false,
          {},
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        changeIndividualUserNameFail,
      ));
  })),
);

const getAccountsSummaryEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getAccountsSummary.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/accounts-summary';

    const payload = {
      customerId: state$.value.crm.individualId as number,
      userTypeRelation: UserTypeRelation.Individual,
    };

    GlobalService
      .crmService
      .getAccountsSummary(payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getAccountsSummarySuccess,
          getAccountsSummaryFailed,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, getAccountsSummaryFailed));
  })),
);

const internalTransferEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(internalTransfer.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/internalTransfer';
    const { amount } = action.payload;

    const payload = {
      amount,
      account_id: state$.value.crm.fundPortalAccountId,
      to_account_id: state$.value.crm.accountDetails.account_id,
      currency_code: CurrencyEnum.EUR,
      tenant_id: config.auth.tenantId,
      // Mandatory parameters, static for all calls
      out_of_fund_cash: true,
      notes: '',
    };

    GlobalService
      .crmService
      .internalTransfer(payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          internalTransferSuccess,
          internalTransferFailed,
        );
      }));
  })),
);

const searchTradingPlansEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(searchTradingPlans.type, getCountryListSuccess.type, accountDetailsCompleted.type),
  filter(() => !isUserVerifiedStatus(state$.value.crm.individualExtendedInfo)
    || !!state$.value.crm.accountDetails.trading_plan_reference_id),
  debounceTime(PredefinedMilisecondsTypeEnum.oneSecond), // To prevent concurrent calls
  mergeMap(action => new Observable((observer: Observer<SearchTradingPlansRequest>) => {
    const callName = 'crm/searchTradingPlans';
    const isUserVerified = isUserVerifiedStatus(state$.value.crm.individualExtendedInfo);

    const request: SearchTradingPlansRequest = {
      page_number: FIRST_PAGE_NUMBER,
      page_size: ALL_TRANSACTIONS_PAGE_SIZE,
      order_column: 'ReferenceId',
      order_ascending: true,
      tenant_id: config.auth.tenantId,
      is_demo: true,
      reference_id: state$.value.crm.accountDetails.trading_plan_reference_id,
    };

    if (!isUserVerified) {
      request.is_default = true;
    }

    GlobalService
      .crmService
      .searchTradingPlans(request)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          searchTradingPlansSuccess,
          searchTradingPlansFail,
        );
      }));
  })),
);


export default combineEpics(
  extendedInfoEpic,
  getAccountReferenceIdListEpic,
  accountsReadyEpic,
  accountInfoReadyEpic,
  getBalancesEpic,
  getCountryListEpic,
  enrollEpic,
  enrollCallQueueEpic,
  enrollTransitionsEpic,
  enrollPersonalDetailsEpic,
  enrollTaxDetailsEpic,
  enrollAddressesBatchEpic,
  getEnrollFinancialQuestionnaireEpic,
  getTradingPlatformsEpic,
  refreshSubscriptionEpic,
  createIndividualEpic,
  getOnboardingLegalAndQuestionsEpic,
  getLegalDeclarationsEpic,
  getLegalDeclarationsDynamicFileNameEpic,
  updateIndividualTransitionToForVerificationEpic,
  updateIndividualTransitionToForActiveEpic,
  getAccountsSummaryEpic,
  getIndividualUserNameEpic,
  changeIndividualUserNameEpic,
  internalTransferEpic,
  searchTradingPlansEpic,
);
